/**
 * Compose `middleware` returning
 * a fully valid middleware comprised
 * of all those which are passed.
 *
 * @param {Array} middleware
 * @return {Function}
 * @api public
 */

function compose (middleware) {
  if (!Array.isArray(middleware)) throw new TypeError('Middleware stack must be an array!')
  for (const fn of middleware) {
    if (typeof fn !== 'function') throw new TypeError('Middleware must be composed of functions!')
  }

  /**
   * @param {Object} context
   * @return {Promise}
   * @api public
   */

  return function (context, next) {
    // last called middleware #
    let index = -1;
    return dispatch(0)
    function dispatch (i) {
      if (i <= index) return Promise.reject(new Error('next() called multiple times'))
      index = i;
      let fn = middleware[i];
      if (i === middleware.length) fn = next;
      if (!fn) return Promise.resolve()
      try {
        return Promise.resolve(fn(context, dispatch.bind(null, i + 1)));
      } catch (err) {
        return Promise.reject(err)
      }
    }
  }
}

// import EventEmitter from 'events'

class BotHistory {
  constructor() {
    // super()
    this.items = [];
  }

  // on (event, callback) {
  //   // Listen for event...
  // }

  push(data) {
    data.time = Date.now();
    this.items.push(data);
  }

  state() {
    return this.items.reduce(
      (state, obj) => Object.assign({}, state, obj.data),
      {}
    )
  }

  middleware() {
    return async (ctx, next) => {
      // console.log('history.middleware.context', JSON.stringify(ctx))
      if (ctx.event) {
        const m = (ctx.event = normalizeMessage(ctx.event));
        m.sender = 'you';
        m.recipient = 'bot';
        this.push(JSON.parse(JSON.stringify(m)));
      }
      ctx.state = this.state();
      await next();
      if (ctx.outgoing) {
        ctx.outgoing = normalizeMessages(ctx.outgoing);
        ctx.outgoing.forEach(m => {
          m.sender = 'bot';
          m.recipient = 'you';
          if (m.text) {
            // console.log('history.state', this.state())
            m = Object.assign({}, m, {
              text: template(m.text, this.state()),
            });
          }
          this.push(JSON.parse(JSON.stringify(m)));
        });
      }
    }
  }
}

function normalizeMessages(messages) {
  return toArray(messages).map(normalizeMessage)
}

function normalizeMessage(message) {
  if (typeof message === 'string') {
    message = { text: message };
  }

  if (message.quick_replies) {
    message.quick_replies = normalizeQuickReplies(message.quick_replies);
  }

  if (typeof message.text !== 'string') {
    console.log('Unexpected message format', message);
  }

  if (message.payload && message.payload.indexOf('?') > -1) {
    const { payload } = message;
    message.data = Object.assign(
      unserialize(payload.slice(payload.indexOf('?'))),
      message.data || {}
    );
  }

  return message
}

function normalizeQuickReplies(quickReplies) {
  return toArray(quickReplies).map(normalizeQuickReply)
}

function normalizeQuickReply(quickReply) {
  if (typeof quickReply === 'string') {
    const close = quickReply.indexOf('>');
    if (quickReply.startsWith('<') && close > 1) {
      return {
        content_type: 'text',
        payload: quickReply.slice(1, close).trim(),
        title: quickReply.slice(1 + close).trim(),
      }
    }

    return {
      title: quickReply,
      payload: quickReply,
      content_type: ['user_phone_number', 'location', 'user_email'].includes(
        quickReply
      )
        ? quickReply
        : 'text',
    }
  }

  // if (quickReply.data) {
  //   quickReply.payload =
  //     (quickReply.payload || '') + '?' + serialize(quickReply.data)
  // }

  return quickReply
}

function toArray(a) {
  return Array.isArray(a) ? a : [a]
}

function unserialize(str) {
  const query = str[0] === '#' || str[0] === '?' ? str.slice(1) : str;
  const result = {};
  query.split('&').forEach(part => {
    const item = part.split('=');
    result[decodeURIComponent(item[0])] = decodeURIComponent(item[1]);
  });
  return result
}

function template(tpl, data) {
  let match;

  // Gender
  const gender = /{gender:([^}/]+)\/([^}/]+)\/([^}]+)}/g;
  while ((match = gender.exec(tpl))) {
    // console.log('template.match gender', match, data.gender)
    const noun =
      data.gender && data.gender.value === 'M'
        ? match[1]
        : data.gender && data.gender.value === 'F'
        ? match[2]
        : match[3];
    tpl = tpl.replace(match[0], noun);
  }

  // Range
  // const budget = /{budget:([^}/]+)\/([^}/]+)\/([^}/]+)}/g
  // while ((match = budget.exec(tpl))) {
  //   const str =
  //     !data.budget || (!data.budget[0] && !data.budget[1])
  //       ? match[3]
  //       : (data.budget[0] ? match[1] + data.budget[0] : '') +
  //         ' ' +
  //         (data.budget[1] ? match[2] + data.budget[1] : '')
  //   console.log('match budget', str)
  //   tpl = tpl.replace(match[0], str)
  // }

  // Normal
  const re = /{([^}]+)?}/g;
  while ((match = re.exec(tpl))) {
    const text =
      data[match[1]] && data[match[1]].model
        ? data[match[1]].value
        : data[match[1]];
    tpl = tpl.replace(match[0], encodeInnerHTML(text || ''));
  }
  return tpl
}

// XSS! Only safe as a value of innerHTML
function encodeInnerHTML(str) {
  const temp = document.createElement('div');
  temp.textContent = str;
  return temp.innerHTML
}

/**
 * Memoize function
 *
 * @param func - Expensive function
 * @param timeout - Milliseconds to wait before running the function again
 * @returns Function that returns optimistic value of `func`
 */

/**
 * Runs functions optimistically
 *
 * @param func - Expensive function
 * @param timeout - Milliseconds to wait before running the function again
 * @returns Function that returns optimistic value of `func`
 */
function optimist(func, timeout = 60000) {
  const cache = {};
  const time = {};
  const f = function(a, b, c) {
    const key = JSON.stringify([a, b, c]);
    if (!cache[key]) {
      time[key] = Date.now() + timeout;
      return (cache[key] = func(a, b, c))
    }
    if (time[key] < Date.now()) {
      time[key] = Date.now() + timeout;
      Promise.resolve(func(a, b, c))
        .then(val => (cache[key] = val))
        .catch(() => console.log('optimist catch', a, b, c));
    }
    return cache[key]
  };
  f.set = (key, value) => (cache[JSON.stringify(key)] = value);
  f.reset = (a, b, c) => (cache[JSON.stringify([a, b, c])] = null);
  return f
}

function serialize(obj) {
  const str = [];
  for (const p in obj) {
    if (obj.hasOwnProperty(p) && obj[p]) {
      str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
    }
  }
  return str.join('&')
}

function ls(key, value) {
  try {
    if (typeof value === 'undefined') {
      return JSON.parse(window.localStorage[key] || 'null')
    } else {
      window.localStorage[key] = JSON.stringify(value);
      return value
    }
  } catch (e) {}
}

// The Connected Loop app will run dreambuddy only in browser
// and append ?kiosk=1 to the URL
const kiosk = process.browser && window.location.search.includes('kiosk=1');
const detectedPageType = 'dreambuddy';
const detectedChannel = process.browser
  ? window.innerWidth < 500
    ? 'mobile-website'
    : 'website'
  : 'mobile-website';

const BUDGET_MAX = 105;
const AGE_MAX = 30;
const optimistGetJSON = optimist((get, url) => get(url));

const decisionTree = {
  fallback: {
    redirect: process.browser ? 'intro' : 'hitl',
  },

  gift: {
    redirect: 'intro',
  },

  hitl: ({ __, event }) =>
    event.platform === 'messenger'
      ? {
          enter: ctx => ctx.passThreadControl(),
          text: __('confirm engagor'),
          quick_replies: [
            {
              text: __('yes'),
            },
            {
              text: __('no'),
              model: 'hitl',
              value: 'gift',
              next: 'age',
            },
          ],
        }
      : {
          redirect: 'menu',
        },

  menu: ({ __, event }) => ({
    text: __('greeting'),
    data: {
      moreIds: '',
      seen: '',
      clicked: '',
    },
    quick_replies: [
      {
        text: __('talk to dreamland'),
        model: 'hitl',
        value: 'human',
      },
      {
        text: __('find a gift'),
        model: 'hitl',
        value: 'gift',
        next: 'age',
      },
    ],
  }),

  letsgo: {
    redirect: 'intro',
  },

  restart: {
    redirect: 'intro',
  },
  gender: modelOrSuggestions('age'),
  after_gender: modelOrSuggestions('age'),

  intro: ({ __ }) => ({
    enter({ state, nextNode }) {
      const data = (nextNode.data = nextNode.data || {});
      if (!state.session_id) {
        data.session_id = Array.apply(null, Array(20))
          .map(
            () =>
              'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'[
                Math.floor(Math.random() * 62)
              ]
          )
          .join('');
      }
      if (!state.lang && lang) {
        data.lang = lang;
      }
    },
    text: __('intro text'),
    next: 'age',
    quick_replies: [
      {
        next: 'age',
        model: 'intro',
        value: 'start',
        title: __('start'),
      },
    ],
  }),

  age: ({ __ }) => ({
    text: __('age text'),
    input: {
      type: kiosk ? 'ageSlider' : 'number',
      model: 'age',
      textual: __('age textual'),
      step: 1,
      min: 0,
      max: AGE_MAX,
      placeholder: __('age placeholder'),
      alternative: -1,
      alternativeText: __('all ages'),
      submit: __('continue'),
    },
    quick_replies: [
      {
        next: 'after_age',
        model: 'age',
        value: '1',
        title: __('0 - 2 j.'),
      },
      {
        next: 'after_age',
        model: 'age',
        value: '4',
        title: '3 - 5',
      },
      {
        next: 'after_age',
        model: 'age',
        value: '7',
        title: '6 - 8',
      },
      {
        next: 'after_age',
        model: 'age',
        value: '10',
        title: '9 - 11',
      },
      {
        next: 'after_age',
        model: 'age',
        value: '15',
        title: '12 - 18',
      },
      {
        next: 'after_age',
        model: 'age',
        value: '20',
        title: '19 - 99',
      },
    ],
    next: 'after_age',
  }),
  after_age: modelOrSuggestions('budget'),

  budget: ({ __, lang }) => ({
    enter({ getJSON, state, nextNode }) {
      optimistGetJSON(getJSON, '/api/categories');
      optimistGetJSON(getJSON, '/api/categoryFilters');
      const data = (nextNode.data = nextNode.data || {});
      if (!state.session_id) {
        data.session_id = Array.apply(null, Array(20))
          .map(
            () =>
              'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'[
                Math.floor(Math.random() * 62)
              ]
          )
          .join('');
      }
      if (!state.lang && lang) {
        data.lang = lang;
      }
    },
    text: __('budget text'),
    input: {
      type: 'budget',
      model: 'budget',
      // 105 means no limit
      options: [
        {
          title: __('budget doesnt matter'),
          value: __('budget doesnt matter'),
          min: '0',
          max: BUDGET_MAX.toString(),
          icon: `<svg width="26" height="24" viewBox="0 0 26 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.0008 19.9793C18.4929 19.9793 22.9452 15.5271 22.9452 10.035C22.9452 4.54292 18.4929 0.0906982 13.0008 0.0906982C7.50874 0.0906982 3.05652 4.54292 3.05652 10.035C3.05652 15.5271 7.50874 19.9793 13.0008 19.9793Z" fill="#FAA41A" stroke="#FAA41A" stroke-width="0.1814" stroke-miterlimit="10"/><path d="M13.0009 18.8728C17.8818 18.8728 21.8387 14.916 21.8387 10.0351C21.8387 5.15408 17.8818 1.19727 13.0009 1.19727C8.1199 1.19727 4.16309 5.15408 4.16309 10.0351C4.16309 14.916 8.1199 18.8728 13.0009 18.8728Z" fill="#F4D20F" stroke="#FAA41A" stroke-width="0.1814" stroke-miterlimit="10"/><path d="M15.1698 12.7534C14.8831 12.9154 14.3097 13.1648 13.649 13.1648C12.9759 13.1648 12.3526 12.9279 11.8913 12.4417C11.5548 12.0678 11.3054 11.5317 11.2057 10.8087H14.7335V10.1979H11.1185C11.1185 10.1106 11.1185 10.0483 11.1185 9.97352C11.1185 9.799 11.1309 9.61201 11.1434 9.44996H14.7335V8.82667H11.2556C11.3802 8.16598 11.6296 7.60502 12.0035 7.24351C12.4274 6.79475 12.9759 6.5579 13.649 6.5579C14.21 6.5579 14.7335 6.71995 15.0452 6.90694L15.2945 6.0842C14.9455 5.89721 14.372 5.68529 13.6365 5.68529C12.6144 5.68529 11.8041 6.05926 11.1683 6.71995C10.6697 7.23105 10.2957 7.95406 10.1461 8.82667H9.32339V9.44996H10.0589C10.0464 9.62448 10.0339 9.81146 10.0339 10.0109C10.0339 10.0732 10.0339 10.1356 10.0339 10.1979H9.32339V10.8087H10.0963C10.2085 11.7063 10.5201 12.4293 10.9938 12.9528C11.6171 13.6759 12.5146 14.0623 13.5244 14.0623C14.3471 14.0623 14.9953 13.8005 15.3818 13.5387L15.1698 12.7534Z" fill="#FAA41A"/><path d="M26 19.9667H0V23.4332H26V19.9667Z" fill="#F4D20F"/><path d="M26 23.308H0V23.8722H26V23.308Z" fill="#FAA41A"/><path d="M5.89371 19.9667H3.30151V23.3062H5.89371V19.9667Z" fill="#F2E6A6"/><path d="M5.89371 23.308H3.30151V23.8722H5.89371V23.308Z" fill="#F2C380"/><path d="M3.30152 19.9667H0.18689V23.3062H3.30152V19.9667Z" fill="#F8B41A"/><path d="M24.8717 19.9667H16.8846V23.3062H24.8717V19.9667Z" fill="#F8B41A"/></svg>`,
        },
        {
          title: '0 - 20 €',
          value: '0-20',
          min: '0',
          max: '20',
          icon: `<svg width="26" height="28" viewBox="0 0 26 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.665 19.4634C18.0153 19.4634 22.3525 15.1261 22.3525 9.77587C22.3525 4.42561 18.0153 0.0883789 12.665 0.0883789C7.31477 0.0883789 2.97754 4.42561 2.97754 9.77587C2.97754 15.1261 7.31477 19.4634 12.665 19.4634Z" fill="#FAA41A" stroke="#FAA41A" stroke-width="0.176714" stroke-miterlimit="10"/><path d="M12.6652 18.3854C17.4201 18.3854 21.2747 14.5308 21.2747 9.77585C21.2747 5.02094 17.4201 1.16632 12.6652 1.16632C7.91028 1.16632 4.05566 5.02094 4.05566 9.77585C4.05566 14.5308 7.91028 18.3854 12.6652 18.3854Z" fill="#F4D20F" stroke="#FAA41A" stroke-width="0.176714" stroke-miterlimit="10"/><path d="M14.7781 12.7597C14.4988 12.9176 13.9401 13.1605 13.2965 13.1605C12.6408 13.1605 12.0336 12.9297 11.5842 12.4561C11.2564 12.0918 11.0135 11.5696 10.9163 10.8653H14.353V10.2702H10.8313C10.8313 10.1852 10.8313 10.1245 10.8313 10.0517C10.8313 9.88164 10.8435 9.69948 10.8556 9.54161H14.353V8.93442H10.9649C11.0863 8.2908 11.3292 7.74433 11.6935 7.39216C12.1064 6.95498 12.6408 6.72425 13.2965 6.72425C13.843 6.72425 14.353 6.88212 14.6566 7.06428L14.8995 6.26278C14.5595 6.08063 14.0009 5.87418 13.2844 5.87418C12.2886 5.87418 11.4992 6.2385 10.8799 6.88212C10.3942 7.38001 10.0298 8.08436 9.88411 8.93442H9.08262V9.54161H9.79911C9.78696 9.71163 9.77482 9.89379 9.77482 10.0881C9.77482 10.1488 9.77482 10.2095 9.77482 10.2702H9.08262V10.8653H9.83554C9.94483 11.7396 10.2484 12.444 10.7099 12.954C11.3171 13.6584 12.1914 14.0348 13.1751 14.0348C13.9766 14.0348 14.6081 13.7798 14.9845 13.5248L14.7781 12.7597Z" fill="#FAA41A"/><path d="M25.3285 19.451H0V22.828H25.3285V19.451Z" fill="#F4D20F"/><path d="M25.3285 22.7061H0V23.2556H25.3285V22.7061Z" fill="#FAA41A"/><path d="M5.74156 19.451H3.21631V22.7043H5.74156V19.451Z" fill="#F2E6A6"/><path d="M5.74156 22.7061H3.21631V23.2556H5.74156V22.7061Z" fill="#F2C380"/><path d="M3.21632 19.451H0.182129V22.7043H3.21632V19.451Z" fill="#F8B41A"/><path d="M24.2295 19.451H16.4487V22.7043H24.2295V19.451Z" fill="#F8B41A"/><path d="M26.0001 23.2433H0.671631V26.6203H26.0001V23.2433Z" fill="#F4D20F"/><path d="M26.0001 26.4966H0.671631V27.0462H26.0001V26.4966Z" fill="#FAA41A"/><path d="M6.41295 23.2433H3.8877V26.4966H6.41295V23.2433Z" fill="#F2E6A6"/><path d="M6.41295 26.4966H3.8877V27.0462H6.41295V26.4966Z" fill="#F2C380"/><path d="M3.8877 23.2433H0.853516V26.4966H3.8877V23.2433Z" fill="#F8B41A"/><path d="M24.8991 23.2433H17.1184V26.4966H24.8991V23.2433Z" fill="#F8B41A"/></svg>`,
        },
        {
          title: '20 - 50 €',
          value: '20-50',
          min: '20',
          max: '50',
          icon: `<svg width="26" height="30" viewBox="0 0 26 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.1642 18.735C18.3142 18.735 22.4892 14.56 22.4892 9.41001C22.4892 4.25998 18.3142 0.0850525 13.1642 0.0850525C8.01416 0.0850525 3.83923 4.25998 3.83923 9.41001C3.83923 14.56 8.01416 18.735 13.1642 18.735Z" fill="#FAA41A" stroke="#FAA41A" stroke-width="0.170101" stroke-miterlimit="10"/><path d="M13.1642 17.6973C17.7411 17.6973 21.4515 13.987 21.4515 9.40999C21.4515 4.83302 17.7411 1.12265 13.1642 1.12265C8.5872 1.12265 4.87683 4.83302 4.87683 9.40999C4.87683 13.987 8.5872 17.6973 13.1642 17.6973Z" fill="#F4D20F" stroke="#FAA41A" stroke-width="0.170101" stroke-miterlimit="10"/><path d="M15.1997 11.7687C14.9308 11.9207 14.3931 12.1545 13.7736 12.1545C13.1423 12.1545 12.5579 11.9324 12.1254 11.4765C11.8098 11.1258 11.576 10.6232 11.4825 9.94518H14.7905V9.3724H11.4006C11.4006 9.29058 11.4006 9.23213 11.4006 9.16199C11.4006 8.99834 11.4123 8.823 11.424 8.67104H14.7905V8.08657H11.5292C11.6461 7.46703 11.8799 6.94101 12.2306 6.60202C12.628 6.1812 13.1423 5.95911 13.7736 5.95911C14.2996 5.95911 14.7905 6.11107 15.0828 6.28641L15.3166 5.51491C14.9893 5.33957 14.4516 5.14085 13.7619 5.14085C12.8034 5.14085 12.0435 5.49153 11.4474 6.11107C10.9798 6.59033 10.6291 7.26832 10.4889 8.08657H9.71736V8.67104H10.407C10.3953 8.83469 10.3837 9.01003 10.3837 9.19706C10.3837 9.25551 10.3837 9.31396 10.3837 9.3724H9.71736V9.94518H10.4421C10.5473 10.7868 10.8395 11.4648 11.2837 11.9558C11.8682 12.6337 12.7098 12.9961 13.6567 12.9961C14.4282 12.9961 15.036 12.7506 15.3984 12.5052L15.1997 11.7687Z" fill="#FAA41A"/><path d="M25.3537 18.7231H0.973022V21.9737H25.3537V18.7231Z" fill="#F4D20F"/><path d="M25.3537 21.8563H0.973022V22.3853H25.3537V21.8563Z" fill="#FAA41A"/><path d="M6.4996 18.7231H4.06885V21.8546H6.4996V18.7231Z" fill="#F2E6A6"/><path d="M6.4996 21.8563H4.06885V22.3853H6.4996V21.8563Z" fill="#F2C380"/><path d="M4.07054 18.7231H1.1499V21.8546H4.07054V18.7231Z" fill="#F8B41A"/><path d="M24.2956 18.7231H16.806V21.8546H24.2956V18.7231Z" fill="#F8B41A"/><path d="M26 22.3734H1.61938V25.6241H26V22.3734Z" fill="#F4D20F"/><path d="M26 25.505H1.61938V26.034H26V25.505Z" fill="#FAA41A"/><path d="M7.14596 22.3734H4.71521V25.505H7.14596V22.3734Z" fill="#F2E6A6"/><path d="M7.14596 25.505H4.71521V26.034H7.14596V25.505Z" fill="#F2C380"/><path d="M4.7152 22.3734H1.79456V25.505H4.7152V22.3734Z" fill="#F8B41A"/><path d="M24.942 22.3734H17.4524V25.505H24.942V22.3734Z" fill="#F8B41A"/><path d="M24.3806 26.0221H0V29.2727H24.3806V26.0221Z" fill="#F4D20F"/><path d="M24.3806 29.1537H0V29.6827H24.3806V29.1537Z" fill="#FAA41A"/><path d="M5.52657 26.0221H3.09583V29.1537H5.52657V26.0221Z" fill="#F2E6A6"/><path d="M5.52657 29.1537H3.09583V29.6827H5.52657V29.1537Z" fill="#F2C380"/><path d="M3.09581 26.0221H0.175171V29.1537H3.09581V26.0221Z" fill="#F8B41A"/><path d="M23.3226 26.0221H15.833V29.1537H23.3226V26.0221Z" fill="#F8B41A"/></svg>`,
        },
        {
          title: '50 - 100 €',
          value: '50-100',
          min: '50',
          max: '100',
          icon: `<svg width="26" height="33" viewBox="0 0 26 33" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.6304 18.0546C18.5934 18.0546 22.6167 14.0313 22.6167 9.06826C22.6167 4.10525 18.5934 0.0819397 13.6304 0.0819397C8.66735 0.0819397 4.64404 4.10525 4.64404 9.06826C4.64404 14.0313 8.66735 18.0546 13.6304 18.0546Z" fill="#FAA41A" stroke="#FAA41A" stroke-width="0.163924" stroke-miterlimit="10"/><path d="M13.6304 17.0547C18.0412 17.0547 21.6168 13.479 21.6168 9.06829C21.6168 4.65753 18.0412 1.08191 13.6304 1.08191C9.21967 1.08191 5.64404 4.65753 5.64404 9.06829C5.64404 13.479 9.21967 17.0547 13.6304 17.0547Z" fill="#F4D20F" stroke="#FAA41A" stroke-width="0.163924" stroke-miterlimit="10"/><path d="M15.5919 11.7771C15.3328 11.9235 14.8146 12.1488 14.2176 12.1488C13.6093 12.1488 13.0461 11.9348 12.6293 11.4955C12.3251 11.1575 12.0998 10.6731 12.0097 10.0198H15.1976V9.4678H11.9308C11.9308 9.38895 11.9308 9.33263 11.9308 9.26504C11.9308 9.10733 11.9421 8.93836 11.9534 8.79191H15.1976V8.22867H12.0547C12.1674 7.63163 12.3927 7.12471 12.7306 6.79803C13.1136 6.3925 13.6093 6.17846 14.2176 6.17846C14.7245 6.17846 15.1976 6.32491 15.4793 6.49388L15.7046 5.7504C15.3891 5.58143 14.871 5.38992 14.2063 5.38992C13.2826 5.38992 12.5504 5.72787 11.9759 6.32491C11.5253 6.78677 11.1874 7.44013 11.0522 8.22867H10.3087V8.79191H10.9733C10.9621 8.94962 10.9508 9.11859 10.9508 9.29883C10.9508 9.35516 10.9508 9.41148 10.9508 9.4678H10.3087V10.0198H11.0071C11.1085 10.8309 11.3901 11.4842 11.8182 11.9573C12.3814 12.6107 13.1925 12.9599 14.105 12.9599C14.8484 12.9599 15.4342 12.7233 15.7834 12.4868L15.5919 11.7771Z" fill="#FAA41A"/><path d="M25.3771 18.0431H1.88184V21.1757H25.3771V18.0431Z" fill="#F4D20F"/><path d="M25.3771 21.0626H1.88184V21.5724H25.3771V21.0626Z" fill="#FAA41A"/><path d="M7.20771 18.0431H4.86523V21.061H7.20771V18.0431Z" fill="#F2E6A6"/><path d="M7.20771 21.0626H4.86523V21.5724H7.20771V21.0626Z" fill="#F2C380"/><path d="M4.86682 18.0431H2.05225V21.061H4.86682V18.0431Z" fill="#F8B41A"/><path d="M24.3575 18.0431H17.1399V21.061H24.3575V18.0431Z" fill="#F8B41A"/><path d="M26.0001 21.5609H2.50488V24.6935H26.0001V21.5609Z" fill="#F4D20F"/><path d="M26.0001 24.5788H2.50488V25.0886H26.0001V24.5788Z" fill="#FAA41A"/><path d="M7.83076 21.5609H5.48828V24.5788H7.83076V21.5609Z" fill="#F2E6A6"/><path d="M7.83076 24.5788H5.48828V25.0886H7.83076V24.5788Z" fill="#F2C380"/><path d="M5.48816 21.5609H2.67358V24.5788H5.48816V21.5609Z" fill="#F8B41A"/><path d="M24.9803 21.5609H17.7627V24.5788H24.9803V21.5609Z" fill="#F8B41A"/><path d="M24.4393 25.0771H0.944092V28.2097H24.4393V25.0771Z" fill="#F4D20F"/><path d="M24.4393 28.0949H0.944092V28.6047H24.4393V28.0949Z" fill="#FAA41A"/><path d="M6.27021 25.0771H3.92773V28.0949H6.27021V25.0771Z" fill="#F2E6A6"/><path d="M6.27021 28.0949H3.92773V28.6047H6.27021V28.0949Z" fill="#F2C380"/><path d="M3.92761 25.0771H1.11304V28.0949H3.92761V25.0771Z" fill="#F8B41A"/><path d="M23.4197 25.0771H16.2021V28.0949H23.4197V25.0771Z" fill="#F8B41A"/><path d="M23.4952 28.6785H0V31.8111H23.4952V28.6785Z" fill="#F4D20F"/><path d="M23.4952 31.6964H0V32.2062H23.4952V31.6964Z" fill="#FAA41A"/><path d="M5.32587 28.6785H2.9834V31.6964H5.32587V28.6785Z" fill="#F2E6A6"/><path d="M5.32587 31.6964H2.9834V32.2062H5.32587V31.6964Z" fill="#F2C380"/><path d="M2.98352 28.6785H0.168945V31.6964H2.98352V28.6785Z" fill="#F8B41A"/><path d="M22.4756 28.6785H15.2581V31.6964H22.4756V28.6785Z" fill="#F8B41A"/></svg>`,
        },
        {
          title: '+100 €',
          value: '+100',
          min: '100',
          max: BUDGET_MAX.toString(),
          icon: `<svg width="26" height="36" viewBox="0 0 26 36" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.6302 18.0546C18.5932 18.0546 22.6166 14.0313 22.6166 9.06829C22.6166 4.10528 18.5932 0.0819702 13.6302 0.0819702C8.66723 0.0819702 4.64392 4.10528 4.64392 9.06829C4.64392 14.0313 8.66723 18.0546 13.6302 18.0546Z" fill="#FAA41A" stroke="#FAA41A" stroke-width="0.163924" stroke-miterlimit="10"/><path d="M13.6303 17.0547C18.0411 17.0547 21.6167 13.479 21.6167 9.06829C21.6167 4.65753 18.0411 1.08191 13.6303 1.08191C9.21955 1.08191 5.64392 4.65753 5.64392 9.06829C5.64392 13.479 9.21955 17.0547 13.6303 17.0547Z" fill="#F4D20F" stroke="#FAA41A" stroke-width="0.163924" stroke-miterlimit="10"/><path d="M15.592 11.7771C15.3329 11.9236 14.8148 12.1489 14.2177 12.1489C13.6094 12.1489 13.0462 11.9348 12.6294 11.4955C12.3252 11.1576 12.0999 10.6732 12.0098 10.0198H15.1978V9.46783H11.931C11.931 9.38898 11.931 9.33266 11.931 9.26507C11.931 9.10736 11.9422 8.93839 11.9535 8.79194H15.1978V8.2287H12.0549C12.1675 7.63166 12.3928 7.12474 12.7308 6.79806C13.1138 6.39253 13.6094 6.17849 14.2177 6.17849C14.7246 6.17849 15.1978 6.32494 15.4794 6.49391L15.7047 5.75043C15.3893 5.58146 14.8711 5.38995 14.2065 5.38995C13.2827 5.38995 12.5505 5.7279 11.976 6.32494C11.5254 6.7868 11.1875 7.44016 11.0523 8.2287H10.3088V8.79194H10.9734C10.9622 8.94965 10.9509 9.11862 10.9509 9.29886C10.9509 9.35519 10.9509 9.41151 10.9509 9.46783H10.3088V10.0198H11.0072C11.1086 10.8309 11.3902 11.4842 11.8183 11.9574C12.3816 12.6107 13.1926 12.9599 14.1051 12.9599C14.8486 12.9599 15.4343 12.7234 15.7835 12.4868L15.592 11.7771Z" fill="#FAA41A"/><path d="M25.3771 18.0431H1.88184V21.1757H25.3771V18.0431Z" fill="#F4D20F"/><path d="M25.3771 21.0626H1.88184V21.5724H25.3771V21.0626Z" fill="#FAA41A"/><path d="M7.20771 18.0431H4.86523V21.061H7.20771V18.0431Z" fill="#F2E6A6"/><path d="M7.20771 21.0626H4.86523V21.5724H7.20771V21.0626Z" fill="#F2C380"/><path d="M4.86682 18.0431H2.05225V21.061H4.86682V18.0431Z" fill="#F8B41A"/><path d="M24.3575 18.0431H17.1399V21.061H24.3575V18.0431Z" fill="#F8B41A"/><path d="M26 21.5609H2.50476V24.6935H26V21.5609Z" fill="#F4D20F"/><path d="M26 24.5788H2.50476V25.0886H26V24.5788Z" fill="#FAA41A"/><path d="M7.83063 21.5609H5.48816V24.5788H7.83063V21.5609Z" fill="#F2E6A6"/><path d="M7.83063 24.5788H5.48816V25.0886H7.83063V24.5788Z" fill="#F2C380"/><path d="M5.48816 21.5609H2.67358V24.5788H5.48816V21.5609Z" fill="#F8B41A"/><path d="M24.9804 21.5609H17.7628V24.5788H24.9804V21.5609Z" fill="#F8B41A"/><path d="M24.4395 25.0771H0.944214V28.2097H24.4395V25.0771Z" fill="#F4D20F"/><path d="M24.4395 28.0949H0.944214V28.6047H24.4395V28.0949Z" fill="#FAA41A"/><path d="M6.27009 25.0771H3.92761V28.095H6.27009V25.0771Z" fill="#F2E6A6"/><path d="M6.27009 28.0949H3.92761V28.6047H6.27009V28.0949Z" fill="#F2C380"/><path d="M3.92761 25.0771H1.11304V28.095H3.92761V25.0771Z" fill="#F8B41A"/><path d="M23.4198 25.0771H16.2023V28.095H23.4198V25.0771Z" fill="#F8B41A"/><path d="M23.4952 28.6785H0V31.8111H23.4952V28.6785Z" fill="#F4D20F"/><path d="M23.4952 31.6964H0V32.2062H23.4952V31.6964Z" fill="#FAA41A"/><path d="M5.32587 28.6785H2.9834V31.6964H5.32587V28.6785Z" fill="#F2E6A6"/><path d="M5.32587 31.6964H2.9834V32.2062H5.32587V31.6964Z" fill="#F2C380"/><path d="M2.9834 28.6785H0.168823V31.6964H2.9834V28.6785Z" fill="#F8B41A"/><path d="M22.4756 28.6785H15.2581V31.6964H22.4756V28.6785Z" fill="#F8B41A"/><path d="M25.8362 32.2094H2.34094V35.342H25.8362V32.2094Z" fill="#F4D20F"/><path d="M25.8362 35.2273H2.34094V35.7371H25.8362V35.2273Z" fill="#FAA41A"/><path d="M7.66682 32.2094H5.32434V35.2273H7.66682V32.2094Z" fill="#F2E6A6"/><path d="M7.66682 35.2273H5.32434V35.7371H7.66682V35.2273Z" fill="#F2C380"/><path d="M5.32422 32.2094H2.50964V35.2273H5.32422V32.2094Z" fill="#F8B41A"/><path d="M24.8165 32.2094H17.5989V35.2273H24.8165V32.2094Z" fill="#F8B41A"/></svg>`,
        },
      ],
      min: 0,
      max: BUDGET_MAX,
      placeholder: __('budget placeholder'),
      submit: __('continue'),
      edit: {
        title: __('edit choices'),
        text: __('edit choices'),
        next: 'choices',
        icon: 'pencil',
        color: '#31aee9',
        data: {
          moreIds: '',
          seen: '',
        },
      },
    },
    quick_replies: [
      {
        next: 'after_budget',
        model: 'budget',
        value: '0-20',
        title: '€ 0 - € 20',
      },
      {
        next: 'after_budget',
        model: 'budget',
        value: '20-35',
        title: '€ 20 - € 35',
      },
      {
        next: 'after_budget',
        model: 'budget',
        value: '35-50',
        title: '€ 35 - € 50',
      },
      {
        next: 'after_budget',
        model: 'budget',
        value: '50-75',
        title: '€ 50 - € 75',
      },
      {
        next: 'after_budget',
        model: 'budget',
        value: '75-100',
        title: '€ 75 - € 100',
      },
    ],
    next: 'after_budget',
  }),
  after_budget: modelOrSuggestions('categories'),

  categories: ({ __ }) => ({
    enter: async ({ nextNode, state, event, getJSON }) => {
      const categories = await optimistGetJSON(getJSON, '/api/categories');
      const categoryFilters = await optimistGetJSON(
        getJSON,
        '/api/categoryFilters'
      );
      categoryFilters.forEach(filter => {
        filter.categories = filter.categories
          .split('\n')
          .map(sanitize)
          .join('\n');
      });
      categories.forEach(cat => {
        cat.key = sanitize(cat.key);
        cat.nl = sanitize(cat.nl);
        cat.fr = sanitize(cat.fr);
      });
      function sanitize(str) {
        return (str || '').replace(/\s+/g, ' ').trim()
      }
      if (process.browser) {
        window.sharedCategories = categories;
      }
      // TODO: Select most appropriate filter
      const filter =
        categoryFilters.find(compareFilter(state)) || categoryFilters[0];

      // Convert to quick replies
      nextNode.categories = filter.categories
        .split('\n')
        .map(key => categories.find(obj => trimLow(obj.key) === trimLow(key)))
        .filter(Boolean);

      if (!nextNode.categories.length) {
        nextNode.categories = categories; //.slice(0, 7)
      }

      nextNode.categories = nextNode.categories.slice(0, 12);
    },
    component: 'categories',
    text: __('category text'),
    quick_replies: [
      {
        title: __('continue'),
        next: 'after_categories',
        icon: 'arrow-right-bold',
        color: '#005ba9',
      },
    ],
    next: 'after_categories',
  }),
  after_categories: {
    redirect: 'suggestions',
  },
  productInfo: async ({ lang, __, getJSON, state }) => ({
    product: await optimistGetJSON(
      getJSON,
      '/api/products/' +
        (state.productId || state.product) +
        '?' +
        serialize({ lang })
    ),
    text: 'Details van product...',
  }),

  suggestions: ({ __, event, postJSON, state, getJSON }) => {
    if (
      ((state.seen || []).length > 18 ||
        (state.moreIds || '').split(',').length > 2) &&
      !state.score &&
      !hasGivenFeedbackRecently()
    ) {
      return {
        redirect: 'score',
      }
    }

    return {
      enter: async ({ lang, nextNode }) => {
        const moreIds = (state.moreIds || '').split(',').filter(Boolean);
        const moreLikeThis = moreIds[moreIds.length - 1];
        const histories = stateToFroomleHistories(state);
        const list = stateToFroomleList(
          state,
          8,
          moreLikeThis ? 'more_like_this' : undefined,
          moreLikeThis ? 'related' : undefined
        );
        const postData = {
          page_type: detectedPageType,
          channel: detectedChannel,
          device_id: state.session_id || '12345678901234567890',
          lists: [list],
          histories,
          language: lang === 'fr' ? 'fr' : 'nl',
          context_item: moreLikeThis,
        };

        // console.log('suggestions.postData', JSON.stringify(postData))
        let res = await withRetry(postJSON)(
          'https://colruyt.froomle.com/api/dreamland/recommendations/requests',
          postData
        );

        if (
          res.Error === 'Unexpected error' &&
          (process.env.NODE_ENV === 'development' ||
            (process.browser &&
              window.location.href.startsWith('http://localhost')))
        ) {
          res = await getJSON('/assets/carousel.json');
        }

        // console.debug('froooomle', res)
        if (res.lists && res.lists[0] && res.lists[0].items.length) {
          // console.log('suggestions res', res)

          res.lists[0].items.forEach(p => {
            p.request_id = res.request_id;
            p.list_name = list.list_name;
          });
          console.log('suggestions ok');
          nextNode.text = fallback(
            __,
            'suggestions:' + (res.status_code || 0),
            'suggestions text'
          );
          nextNode.products = res.lists[0].items;
          nextNode.data = {
            exclude: res.lists[0].items.map(p => {
              return {
                id: p.product,
                reconsumable: false,
              }
            }),
            seen: (state.seen || [])
              .concat(res.lists[0].items.map(p => p.product))
              .filter(uniq),
          };
          nextNode.quick_replies = [
            {
              title: up(__('more')),
              next: 'suggestions',
              icon: 'plus',
              color: '#005ba9',
            },
          ];
        } else if (res.Error === 'timeout') {
          nextNode.text = up(__('timeout'));
          nextNode.products = null;
          nextNode.quick_replies = [
            {
              title: up(__('Herstart')),
              payload: 'restart=1',
            },
          ];
        } else if (!res.lists[0].items.length) {
          console.log('No candidates found');
          nextNode.text = __('No candidates found.');

          const list = stateToFroomleList(
            { seen: state.seen, budget: state.budget },
            8
          );
          const histories = stateToFroomleHistories({ seen: state.seen });

          const res = await postJSON(
            'https://colruyt.froomle.com/api/dreamland/recommendations/requests',
            {
              page_type: detectedPageType,
              channel: detectedChannel,
              device_id: state.session_id || '12345678901234567890',
              lists: [list],
              histories,
              language: lang === 'fr' ? 'fr' : 'nl',
            }
          );
          console.log('response', res);
          if (
            res.lists &&
            res.lists[0] &&
            res.lists[0].items &&
            res.lists[0].items.length
          ) {
            res.lists[0].items.forEach(p => {
              p.request_id = res.request_id;
              p.list_name = list.list_name;
            });
            nextNode.products = res.lists[0].items;
          } else {
            nextNode.products = null;
          }
          nextNode.quick_replies = [
            {
              title: __('Herstart'),
              payload: 'restart=1',
            },
          ];
        } else {
          nextNode.text = __('Unexpected error');
          nextNode.products = null;
          nextNode.quick_replies = [
            {
              title: __('Herstart'),
              payload: 'restart=1',
            },
          ];
        }

        // if ((state.seen || []).length > 2) {
        if (kiosk || event.platform === 'messenger') {
          nextNode.quick_replies.push({
            title: __('giftcard'),
            next: 'giftcards',
            icon: 'present',
            color: '#005ba9',
          });
        } else {
          nextNode.quick_replies.push({
            title: __('giftcard'),
            url: refParam(__('giftcards link')),
            icon: 'present',
            color: '#005ba9',
          });
        }
        if (event.platform !== 'messenger') {
          nextNode.quick_replies.push({
            title: __('edit choices'),
            next: 'choicesAfterSuggestions',
            icon: 'pencil',
            color: '#31aee9',
            data: {
              moreIds: '',
              seen: '',
            },
          });
        }
        // }

        // Note: this code uses likeIds, but "liking" is not available anymore
        setTimeout(() => {
          if (!moreIds.length || !res.request_id) {
            return // Did not click recommendation
          }

          const data = {
            events: [
              {
                device_id: state.session_id || '12345678901234567890',
                event_type: 'item_interaction',
                interaction_type: 'like',
                action_item: moreLikeThis,
                page_type: 'dreambuddy',
                channel: detectedChannel,
                request_id: `${res.request_id}`,
              },
            ],
          };
          console.log('froomle.events', data.events);
          postJSON('https://colruyt.froomle.com/api/dreamland/events', data)
            .then(ok => console.log('froomle.events ok', ok))
            .catch(err => console.error('froomle.events err', err));
        });
      },
      text: 'suggestions:',
      quick_replies: ['<after_suggestions> Oeps'],
    }
  },
  after_suggestions: {
    redirect: 'suggestions',
  },

  score: ({ platform, __ }) => ({
    text: __('score text'),
    input: {
      type: 'score',
      model: 'score',
      value: '-',
      submit: __('continue'),
      next: 'after_score',
      skip: {
        text: __('skip'),
        next: 'suggestions',
        model: 'score',
        value: '-',
      },
      edit: {
        title: __('edit choices'),
        next: 'choices',
        icon: 'pencil',
        color: '#31aee9',
        data: {
          moreIds: '',
          seen: '',
        },
      },
    },
    quick_replies:
      platform === 'messenger'
        ? [5, 4, 3, 2, 1].map(value => ({
            text: __('⭐️').repeat(value),
            model: 'score',
            value,
            next: 'after_score',
          }))
        : [
            {
              text: __('continue'),
              next: 'after_score',
              model: 'score',
              value: '-',
            },
          ],
  }),

  after_score: ({ __, event, postJSON, state, platform }) => {
    rememberFeedbackDate();
    postJSON('/api/feedback', { state, event }).catch(e => {
      console.error('feedback.score', e);
    });
    if (event.data.score > 3 || platform === 'messenger' || kiosk) {
      return { redirect: 'suggestions' }
    }
    return { redirect: 'feedback' }
  },

  feedback: ({ __, event, postJSON, state, platform }) => {
    return {
      text: __('feedback text'),
      input: {
        type: 'textarea',
        model: 'feedback',
        placeholder: __('feedback placeholder'),
        alternative: '-',
        alternativeText: __('feedback empty'),
        submit: __('continue'),
        next: 'after_feedback',
        edit: {
          title: __('edit choices'),
          next: 'choices',
          icon: 'pencil',
          color: '#31aee9',
          data: {
            moreIds: '',
            seen: '',
          },
        },
      },
      quick_replies: [
        {
          text: __('feedback empty'),
          next: 'after_feedback',
        },
      ],
    }
  },

  after_feedback: ({ __, event, postJSON, state }) => {
    rememberFeedbackDate();
    postJSON('/api/feedback', { state, event }).catch(e => {
      console.error('feedback.feedback', e);
    });
    return { redirect: 'suggestions' }
  },

  cart: ({ __ }) => ({
    text: __('cart text'),
    next: 'suggestions',
    quick_replies: [
      {
        next: 'checkout',
        title: up(__('to checkout')),
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-4 -5 24 24" fill="none"><path fill="#fff" d="M14.3 8.6L15.6 3c0-.4-.2-.8-.7-.8H4.3l-.2-1.1C4 .9 3.7.7 3.4.7H.6c-.3 0-.6.2-.6.6v.4c0 .4.3.6.6.6h2l1.8 9c-.4.3-.7.8-.7 1.4 0 .8.7 1.4 1.5 1.4s1.5-.6 1.5-1.4c0-.5-.2-.8-.4-1.1h5.6c-.3.3-.4.6-.4 1 0 .9.7 1.5 1.5 1.5s1.5-.6 1.5-1.4c0-.6-.3-1.1-.9-1.4l.2-.6c0-.4-.2-.8-.6-.8H5.9l-.2-.8h8c.3 0 .5-.2.6-.5z"/></svg>`,
        color: '#2DC76D',
      },
      {
        next: 'suggestions',
        title: up(__('more inspiration')),
        icon: 'plus',
        color: '#005AA6',
      },
    ],
  }),

  cartError: ({ __ }) => ({
    text: __('cart error text'),
    next: 'suggestions',
    quick_replies: [
      {
        next: 'checkout',
        title: up(__('to checkout')),
        icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="-4 -5 24 24" fill="none"><path fill="#fff" d="M14.3 8.6L15.6 3c0-.4-.2-.8-.7-.8H4.3l-.2-1.1C4 .9 3.7.7 3.4.7H.6c-.3 0-.6.2-.6.6v.4c0 .4.3.6.6.6h2l1.8 9c-.4.3-.7.8-.7 1.4 0 .8.7 1.4 1.5 1.4s1.5-.6 1.5-1.4c0-.5-.2-.8-.4-1.1h5.6c-.3.3-.4.6-.4 1 0 .9.7 1.5 1.5 1.5s1.5-.6 1.5-1.4c0-.6-.3-1.1-.9-1.4l.2-.6c0-.4-.2-.8-.6-.8H5.9l-.2-.8h8c.3 0 .5-.2.6-.5z"/></svg>`,
        color: '#2DC76D',
      },
      {
        next: 'suggestions',
        title: up(__('more inspiration')),
        icon: 'plus',
        color: '#005AA6',
      },
    ],
  }),

  checkout() {
    window.top.postMessage({ type: 'checkout' }, '*');
    return {}
  },

  choicesAfterSuggestions: ({ __ }) => ({
    text: __('choices text'),
    component: 'choices',
    quick_replies: [
      {
        title: up(__('more')),
        next: 'suggestions',
        icon: 'plus',
        color: '#005ba9',
      },
    ],
  }),

  choices: ({ __ }) => ({
    text: __('choices text'),
    component: 'choices',
  }),

  giftcards: ({ __, event }) => {
    if (event.platform === 'messenger') {
      return {
        title: __('giftcard'),
        text: __('giftcard messenger text'),
        next: 'suggestions',
        buttons: [
          {
            text: __('giftcard'),
            url: refParam(__('giftcards link')),
          },
        ],
      }
    } else {
      // kiosk
      return {
        text: __('giftcard text'),
        next: 'suggestions',
        quick_replies: [
          {
            next: 'suggestions',
            title: up(__('more inspiration')),
            icon: 'plus',
            color: '#005AA6',
          },
        ],
      }
    }
  },
};

function stateToFroomleHistories(state) {
  if (!(state.clicks || state.exclude)) return
  return {
    likes: state.clicks || [],
    exclude: (state.exclude || []).filter(
      item => !(state.clicks || []).map(excl => excl.id).includes(item.id)
    ),
  }
}

function stateToFroomleList(state, quantity, list_name, module_id) {
  let price;
  if (state.budget) {
    let { budget } = state;
    if (typeof budget === 'string') {
      if (budget.includes('-')) {
        budget = budget.split('-').map(b => parseFloat(b));
      } else {
        const parsed = parseInt(budget, 10);
        budget = Math.max(
          10,
          Math.min(BUDGET_MAX, isNaN(parsed) ? BUDGET_MAX : parsed)
        );
        price = {
          range: {
            min: Math.floor(budget * 0.5),
            max: Math.ceil(budget),
          },
        };
      }
    }
    if (Array.isArray(budget) && (budget[0] || budget[1] < BUDGET_MAX)) {
      budget = budget.map(b => parseFloat(b)).sort((a, b) => a - b);
      if (budget[0] === budget[1]) {
        budget = [budget[0] - 1, budget[0] + 1];
      }
      if (budget[0] < 0) {
        budget[0] = 0;
      }
      if (budget[1] < 1) {
        budget[1] = 1;
      }
      price = {
        range: {
          min: budget[0] || 0,
        },
      };
      if (budget[1] < BUDGET_MAX) {
        price.range.max = parseFloat(budget[1]);
      }
    } else {
      price = {
        range: {
          min: budget[0],
          max: budget[1],
        },
      };
    }
  }

  let age = avgAge(state.age);
  let categories =
    typeof state.categories === 'string'
      ? state.categories.split(',')
      : state.categories
      ? state.categories
      : [];
  return {
    list_name: list_name
      ? list_name
      : state.seen
      ? 'show_more'
      : categories.length > 0
      ? 'discover'
      : 'surprise_me',
    module_id: module_id ? module_id : 'discover',
    list_size: quantity,
    age: age > -1 ? age : undefined,
    dreambuddy_category: categories.length > 0 ? categories : undefined,
    price_min: price.range.min,
    price_max: price.range.max,
  }
}

function compareFilter(state) {
  let age = avgAge(state.age);
  console.log('tree.compareFilter.age', age);
  return f => {
    const { ageMin, ageMax, gender } = decodeFilter(f.filter);
    // Too old
    if (ageMax > -1 && age > -1 && ageMax < Math.min(age, 98)) {
      return false
    }
    // Too young
    if (ageMin > -1 && age > -1 && ageMin > age) {
      return false
    }
    // Other gender
    const g = (state.gender && state.gender.value) || state.gender;
    if (gender && g !== gender) {
      return false
    }
    return true
  }
}

function avgAge(str) {
  const nums = String(str)
    .replace(/\D+/g, '-')
    .split('-')
    .map(t => parseInt(t, 10))
    .filter(Boolean);
  if (!nums.length) {
    return -1
  }
  return Math.floor(Math.min(99, nums.reduce((a, b) => a + b, 0) / nums.length))
}

function decodeFilter(filter) {
  const out = {};
  if (filter.indexOf('F') > -1) {
    out.gender = 'F';
  }
  if (filter.indexOf('M') > -1) {
    out.gender = 'M';
  }
  if (filter.indexOf('-') > 0) {
    const age = /(\d+)?\-(\d+)?/g.exec(filter);
    if (age && age[1]) {
      out.ageMin = parseInt(age[1]);
    }
    if (age && age[2]) {
      out.ageMax = parseInt(age[2]);
    }
  } else {
    const age = /(\d+)?/g.exec(filter);
    if (age && age[1]) {
      out.ageMin = parseInt(age[1]);
      out.ageMax = parseInt(age[1]);
    }
  }
  return out
}

function hasGivenFeedbackRecently() {
  if (!process.browser) {
    return
  }
  const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 36e5).toJSON();
  return ls('Dreambuddy/feedbackDate') || '' > twoWeeksAgo
}

function rememberFeedbackDate() {
  ls('Dreambuddy/feedbackDate', new Date().toJSON());
}

async function koaMiddleware(ctx, next) {
  // Follow decision tree
  const decision = await decide(decisionTree, ctx);
  if (decision) {
    ctx.reply(decision);
  }
  return next()
}

async function ambientLogic(ctx, next) {
  // Follow decision tree
  const decision = await decide(decisionTree, ctx);
  if (decision) {
    ctx.next = decision;
  }
  return next()
}

async function decide(tree, ctx) {
  const { language, next } = ctx.event || {};
  if (!ctx.nextNode && next) {
    ctx.nextNode = tree[next];
  }

  let node = (ctx.nextNode = ctx.nextNode || tree.fallback);
  // Ambient dynamic nodes
  if (typeof node === 'function') {
    try {
      node = ctx.nextNode = await node(ctx);
    } catch (e) {
      Sentry.captureException(e);
    }
  }
  // Legacy dynamic nodes
  if (node.enter) {
    // console.debug('decide enter', node)
    try {
      // Sentry.configureScope(scope => {
      //   scope.setExtra('ctx', ctx)
      // })
      await node.enter(ctx);
    } catch (e) {
      // Sentry.captureException(e)
    }
    // console.debug('decide enter after', ctx)
  }
  if (node.redirect && tree[node.redirect]) {
    ctx.nextNode = tree[node.redirect];
    // console.debug('decide redirect', node.redirect)
    return decide(tree, ctx)
  }
  // TODO remove assertion
  if (node[language] || node.nl || node.en) {
    throw new Error('tree:language nodes')
  }
  return node
}

function withRetry(func) {
  return (a, b) => {
    let done = false;
    return new Promise(resolve => {
      func(a, b)
        .then(success)
        .catch(error);
      setTimeout(() => {
        done ||
          func(a, b)
            .then(success)
            .catch(error);
      }, 2000);
      setTimeout(() => done || resolve({ Error: 'timeout' }), 5000);

      function error(result) {
        done || resolve({ Error: 'Unexpected error' });
        done = true;
      }
      function success(result) {
        done || resolve(result);
        done = true;
      }
    })
  }
}

function up(str) {
  return str.charAt(0).toUpperCase() + str.slice(1)
}

function fallback(__, str, fallback) {
  const translated = __(str);
  return translated === str ? __(fallback) : translated
}

function modelOrSuggestions(model) {
  return {
    enter: ({ nextNode, state }) => {
      if (state.seen) {
        nextNode.redirect = 'suggestions';
      } else {
        nextNode.redirect = model;
      }
    },
    redirect: model,
  }
}

function refParam(url) {
  return url + (url.indexOf('?') > 0 ? '&' : '?') + 'giftfinder=1'
}

function trimLow(str) {
  return (str || '')
    .replace(/\s/g, '')
    .trim()
    .toLowerCase()
}

function uniq(v, i, a) {
  return a.findIndex(b => b === v) === i
}

function translate(translations) {
  const translate = translator(translations);
  return async (ctx, next) => {
    ctx.user = ctx.getUser ? await ctx.getUser() : {};
    Object.defineProperty(ctx, 'lang', {
      enumerable: false,
      get: () =>
        (ctx.user.locale || '').slice(0, 2) ||
        ctx.__lang ||
        (ctx.__lang =
          ctx.state.lang ||
          (ctx.nextNode && ctx.nextNode.data && ctx.nextNode.data.lang) ||
          (process.browser && getBrowserLang()) ||
          ''),
    });
    Object.defineProperty(ctx, '__', {
      enumerable: false,
      value: key =>
        template$1(translate(ctx.lang, key), { ...ctx.user, ...ctx.state }),
    });
    return next()
  }
}

function getBrowserLang() {
  return (window.location.search.indexOf('lang=fr') > -1 && 'fr') || 'nl'
}

function translator(translations) {
  const lookup = Object.create(null);
  for (var i = translations.length - 1; i >= 0; i--) {
    lookup[translations[i].key] = translations[i];
  }

  return (lang, key) => {
    const regex = new RegExp('^' + escapeRegExp(key) + '[d]*$');
    const possibilities = Object.keys(lookup).filter(k => regex.test(k));
    const index = Math.ceil(Math.random() * possibilities.length) - 1;

    const match = lookup[possibilities[index]] || lookup[key];
    return (match && (match[lang] || match.nl)) || key
  }
}
function escapeRegExp(stringToGoIntoTheRegex) {
  return stringToGoIntoTheRegex.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&')
}

function template$1(tpl, data) {
  let match;

  // Gender
  // const gender = /{gender:([^}/]+)\/([^}/]+)\/([^}]+)}/g
  // while ((match = gender.exec(tpl))) {
  //   // console.log('template.match gender', match, data.gender)
  //   const noun =
  //     data.gender && data.gender.value === 'M'
  //       ? match[1]
  //       : data.gender && data.gender.value === 'F'
  //       ? match[2]
  //       : match[3]
  //   tpl = tpl.replace(match[0], noun)
  // }

  // Normal
  const re = /(\s)?{([^}]+)?}/g;
  while ((match = re.exec(tpl))) {
    const text =
      data[match[2]] && data[match[2]].model
        ? data[match[2]].value
        : data[match[2]];
    tpl = tpl.replace(match[0], text ? match[1] + text : '');
  }
  return tpl
}

var translations = [{"id":1,"key":"test","nl":"test","fr":"test","createdAt":"2018-09-12T16:23:39.539Z","updatedAt":"2019-09-05T08:44:59.682Z"},{"id":12,"key":"Cadeaukaart","nl":"Geschenkkaart","fr":"Carte-cadeau","createdAt":"2018-09-17T13:31:41.536Z","updatedAt":"2019-09-17T14:19:20.252Z"},{"id":13,"key":"Herstart","nl":"Begin opnieuw ⏮️","fr":"Recommencer ⏮️","createdAt":"2018-09-17T13:31:55.773Z","updatedAt":"2019-09-19T08:56:10.822Z"},{"id":15,"key":"occasion text","nl":"<p>Voor welk feestje zoek je een cadeautje? 🎁</p>","fr":"<p>Pour quelle occasion cherchez-vous un cadeau ? 🎁</p>","createdAt":"2018-09-19T07:51:40.303Z","updatedAt":"2020-04-01T13:41:23.563Z"},{"id":16,"key":"gender text","nl":"<p>Voor wie is het cadeautje bestemd?</p>","fr":"<p>À qui est destiné le cadeau ?</p>","createdAt":"2018-09-19T07:53:50.064Z","updatedAt":"2019-09-19T08:55:36.903Z"},{"id":17,"key":"he","nl":"Hem ♂️","fr":"Un garçon ♂️","createdAt":"2018-09-19T07:54:15.845Z","updatedAt":"2019-09-19T08:56:01.107Z"},{"id":18,"key":"she","nl":"Haar ♀️","fr":"Une fille ♀️","createdAt":"2018-09-19T07:54:21.124Z","updatedAt":"2019-09-19T08:56:04.896Z"},{"id":19,"key":"doesn't matter","nl":"Maakt niet uit ","fr":"Peu importe","createdAt":"2018-09-19T07:54:32.689Z","updatedAt":"2019-07-31T12:06:49.903Z"},{"id":23,"key":"continue","nl":"Volgende","fr":"Suivant","createdAt":"2018-09-19T07:56:45.597Z","updatedAt":"2020-01-17T12:30:20.341Z"},{"id":24,"key":"age text","nl":"<p>Hoe oud is de gelukkige? 🥳</p>","fr":"<p>Pour quel âge ? 🥳</p>","createdAt":"2018-09-19T07:58:07.319Z","updatedAt":"2020-12-01T16:12:26.195Z"},{"id":25,"key":"age placeholder","nl":"Leeftijd","fr":"Âge","createdAt":"2018-09-19T07:59:21.100Z","updatedAt":"2018-09-26T08:46:34.691Z"},{"id":27,"key":"budget text","nl":"<p>Wat is je budget? 💰</p>","fr":"<p>Pour quel budget ? 💰</p>","createdAt":"2018-09-19T08:00:13.789Z","updatedAt":"2019-09-19T09:06:02.548Z"},{"id":29,"key":"budget placeholder","nl":"euro","fr":"euros","createdAt":"2018-09-19T08:00:39.895Z","updatedAt":"2018-11-19T12:35:45.048Z"},{"id":30,"key":"category text","nl":"Wat vindt de gelukkige leuk?","fr":"<p>Qu'est-ce qui lui ferait plaisir ?</p><p></p>","createdAt":"2018-09-19T08:02:32.374Z","updatedAt":"2020-12-01T16:12:13.190Z"},{"id":31,"key":"surprise me","nl":"Verras mij!","fr":"Surprenez-moi !","createdAt":"2018-09-19T08:03:31.532Z","updatedAt":"2019-09-19T08:57:31.669Z"},{"id":32,"key":"suggestions text","nl":"Tada! Met deze cadeautjes pak je het feestvarken helemaal in! 🎀🎀","fr":"<p>Et hop ! Voilà quelques idées de cadeaux ! 🎀🎀</p>","createdAt":"2018-09-19T08:05:16.747Z","updatedAt":"2019-09-19T09:05:56.581Z"},{"id":33,"key":"more","nl":"Meer...","fr":"En voir plus...","createdAt":"2018-09-19T08:05:43.806Z","updatedAt":"2018-09-25T14:49:36.442Z"},{"id":34,"key":"budget","nl":"budget","fr":"budget","createdAt":"2018-09-19T08:06:17.269Z","updatedAt":"2018-11-19T12:36:23.476Z"},{"id":35,"key":"sidebar greeting","nl":"<h1>Hallo daar!</h1>\n<p>Zoek je een cadeautje?</p>\n<p>Kom, ik zal je helpen!</p>","fr":"<h1>Bonjour !</h1> \n<p>Vous cherchez un cadeau ? </p>\n<p>Je vous donne un coup de main !</p><p></p>","createdAt":"2018-09-21T15:09:23.816Z","updatedAt":"2019-09-19T08:58:46.082Z"},{"id":36,"key":"age textual","nl":"# jaar","fr":"# ans","createdAt":"2018-09-21T15:58:26.027Z","updatedAt":"2018-09-25T14:48:03.176Z"},{"id":37,"key":"# categories","nl":"# categorieën","fr":"# catégories","createdAt":"2018-09-21T16:21:57.339Z","updatedAt":"2018-11-19T12:36:40.955Z"},{"id":38,"key":"budget min text","nl":"min. € # ","fr":"min. € # ","createdAt":"2018-09-21T16:25:57.085Z","updatedAt":"2018-09-21T16:30:15.672Z"},{"id":39,"key":"budget max text","nl":"max. € # ","fr":"max. € # ","createdAt":"2018-09-21T16:26:00.583Z","updatedAt":"2018-09-21T16:30:10.067Z"},{"id":43,"key":"Back","nl":"Terug","fr":"Retour","createdAt":"2018-09-25T13:40:27.489Z","updatedAt":"2018-11-19T12:37:22.735Z"},{"id":44,"key":"view product","nl":"Details bekijken 🧐","fr":"Voir les détails  🧐","createdAt":"2018-09-25T13:51:53.505Z","updatedAt":"2019-09-19T09:02:29.961Z"},{"id":45,"key":"Unexpected error","nl":"Oeps, er ging iets mis. Probeer je het later nog eens?","fr":"Désolé, un problème est survenu ! Merci de réessayer plus tard.","createdAt":"2018-09-25T15:14:52.822Z","updatedAt":"2019-09-19T08:59:18.545Z"},{"id":46,"key":"No candidates found.","nl":"<p>In deze categorieën kon ik niets vinden. Misschien vind je het perfecte geschenk wel tussen de populairste artikelen bij DreamLand:</p>","fr":"<p>Je n'ai malheureusement rien trouvé dans ces catégories. Mais voici les articles les plus demandés chez DreamLand !</p>","createdAt":"2018-09-25T15:15:06.327Z","updatedAt":"2019-09-19T08:59:26.732Z"},{"id":47,"key":"change","nl":"Wijzigen","fr":"Modifier","createdAt":"2018-10-01T14:18:29.146Z","updatedAt":"2018-11-19T12:37:43.984Z"},{"id":49,"key":"timeout","nl":"Oeps, er ging iets mis. Probeer je het later nog eens?","fr":"Désolé, un problème est survenu ! Merci de réessayer plus tard.","createdAt":"2018-10-05T14:12:51.866Z","updatedAt":"2019-09-19T08:59:40.976Z"},{"id":50,"key":"Your choices","nl":"Voorkeuren aanpassen","fr":"Adapter vos préférences","createdAt":"2018-10-10T15:54:20.247Z","updatedAt":"2019-09-19T08:59:47.783Z"},{"id":51,"key":"My choices","nl":"Dit zijn jouw voorkeuren:","fr":"Vos préférences :","createdAt":"2018-10-15T09:06:31.191Z","updatedAt":"2019-09-19T08:59:55.029Z"},{"id":52,"key":"all categories","nl":"Alle categorieën","fr":"Toutes les catégories","createdAt":"2018-10-15T14:25:51.578Z","updatedAt":"2019-09-17T14:39:06.500Z"},{"id":53,"key":"any budget","nl":"Elk budget","fr":"N'importe quel budget","createdAt":"2018-10-15T14:25:59.750Z","updatedAt":"2019-09-19T09:00:07.103Z"},{"id":54,"key":"all ages","nl":"Alle leeftijden","fr":"Tous les âges","createdAt":"2018-10-15T14:26:10.491Z","updatedAt":"2019-09-19T09:00:15.373Z"},{"id":56,"key":"suggestions:0_","nl":"<p>Tada! Met deze cadeautjes pak je de gelukkige helemaal in! 🎀🎀</p>","fr":"<p>Et hop ! Voilà quelques idées de cadeaux ! 🎀🎀</p>","createdAt":"2018-11-15T08:40:17.402Z","updatedAt":"2020-12-01T16:12:43.152Z"},{"id":57,"key":"suggestions:1_","nl":"<p>Oei, ik kon niet meteen iets vinden.  Kan je je persoonlijke voorkeuren aanpassen?</p>","fr":"<p>Je n'ai malheureusement rien trouvé. On peut adapter vos préférences ?</p>","createdAt":"2018-11-15T08:40:31.658Z","updatedAt":"2019-09-19T09:00:34.127Z"},{"id":58,"key":"suggestions:2_","nl":"<p>Oei, bij deze interesses vond ik niets dat binnen je budget past. Kan je jouw budget wat aanpassen?</p>","fr":"<p>Je n'ai malheureusement rien trouvé dans ces centres d'intérêt pour ce budget. On peut adapter le budget ?</p>","createdAt":"2018-11-15T08:40:31.969Z","updatedAt":"2019-09-19T09:00:44.851Z"},{"id":59,"key":"suggestions:3_","nl":"<p>Klik op de 'Meer zoals dit' als je denkt dat de gelukkige iets leuk vindt. Zo kan ik nog leukere cadeautjes tevoorschijn toveren!</p>","fr":"<p>Clique sur 'Plus de ce genre' des cadeaux qui pourraient lui plaire. Je pourrai alors t'en dénicher d'autres encore plus chouettes !</p>","createdAt":"2018-11-15T08:40:34.949Z","updatedAt":"2020-12-01T16:12:53.816Z"},{"id":61,"key":"talk to dreamland","nl":"Stel een vraag 💬","fr":"Poser une question 💬","createdAt":"2019-07-30T16:29:22.971Z","updatedAt":"2019-08-05T12:12:03.313Z"},{"id":62,"key":"find a gift","nl":"Vind een cadeau 🔎","fr":"Trouver un cadeau 🔎","createdAt":"2019-07-30T16:29:27.122Z","updatedAt":"2019-08-05T12:12:23.740Z"},{"id":63,"key":"product info met korting","nl":"De prijs is nu {price}, maar normaal gezien {list_price}","fr":"Le prix est maintenant {price}, mais normalement {list_price}","createdAt":"2019-07-30T16:30:05.848Z","updatedAt":"2019-10-21T07:42:11.421Z"},{"id":64,"key":"product info zonder korting","nl":"De prijs is {price}","fr":"Le prix est {price}","createdAt":"2019-07-30T16:30:11.166Z","updatedAt":"2019-10-21T07:42:31.827Z"},{"id":65,"key":"more like this","nl":"Meer zoals dit 👍","fr":"Plus de ce genre 👍\n","createdAt":"2019-07-30T16:30:36.947Z","updatedAt":"2019-09-19T09:03:00.608Z"},{"id":66,"key":"something else","nl":"Iets anders","fr":"Autre chose","createdAt":"2019-07-30T16:31:00.947Z","updatedAt":"2019-10-21T07:42:44.989Z"},{"id":67,"key":"do you want to continue","nl":"Wil je verdergaan?","fr":"\nVoulez-vous continuer?","createdAt":"2019-07-30T16:31:21.318Z","updatedAt":"2019-08-05T12:13:17.754Z"},{"id":68,"key":"buy","nl":"In winkelwagen 🛒","fr":"Ajouter au panier 🛒","createdAt":"2019-07-30T16:31:37.143Z","updatedAt":"2020-01-17T12:28:49.775Z"},{"id":69,"key":"show details","nl":"Details bekijken 🧐","fr":"Voir les détails 🧐","createdAt":"2019-07-30T16:36:37.025Z","updatedAt":"2019-08-05T12:13:49.464Z"},{"id":70,"key":"greeting","nl":"Hallo {name}, hoe kan ik je helpen?","fr":"Bonjour {name}, comment puis-je vous aider ?","createdAt":"2019-07-30T16:38:53.606Z","updatedAt":"2019-10-03T12:00:52.924Z"},{"id":71,"key":"cart text","nl":"<p>Het artikel is toegevoegd aan je winkelwagen. Ga je meteen naar de kassa of wil je nog meer cadeautips?</p>","fr":"<p>L'article est ajouté au panier. Vous souhaitez passer commande ou ajouter d'autres cadeaux ?</p>","createdAt":"2019-07-31T08:23:42.225Z","updatedAt":"2019-09-19T09:03:49.105Z"},{"id":72,"key":"more inspiration","nl":"Meer cadeautips","fr":"Plus d'idées-cadeaux","createdAt":"2019-07-31T08:23:53.373Z","updatedAt":"2019-09-19T09:03:57.872Z"},{"id":73,"key":"to checkout","nl":"Naar de kassa","fr":"Accéder au panier","createdAt":"2019-07-31T08:23:56.885Z","updatedAt":"2019-09-19T09:04:03.067Z"},{"id":75,"key":"feedback text","nl":"Nog niets gevonden? <p>Kan je me kort vertellen wat er fout liep?</p>","fr":"Toujours rien trouvé ? Avez-vous des suggestions d’améliorations ?","createdAt":"2019-07-31T09:33:40.834Z","updatedAt":"2020-01-17T12:29:47.219Z"},{"id":76,"key":"feedback placeholder","nl":"Wat ging er mis?","fr":"Que suggérez-vous ?","createdAt":"2019-07-31T09:33:44.418Z","updatedAt":"2020-01-17T12:30:00.632Z"},{"id":77,"key":"feedback empty","nl":"Geen feedback","fr":"Aucun feed-back.","createdAt":"2019-07-31T09:34:18.181Z","updatedAt":"2019-09-19T09:04:31.803Z"},{"id":78,"key":"score text","nl":"We zoeken zo meteen verder! Laat je me eerst weten welke score je me zou geven?","fr":"Je continue à chercher ! Mais quel score me donneriez-vous actuellement ?","createdAt":"2019-07-31T09:37:25.187Z","updatedAt":"2020-01-17T12:29:25.497Z"},{"id":79,"key":"0 - 2 j.","nl":"0 - 2 j.","fr":"0 - 2 a.","createdAt":"2019-07-31T13:13:31.164Z","updatedAt":"2019-10-21T07:42:59.264Z"},{"id":82,"key":"giftcards","nl":"Geschenkkaart","fr":"Carte-cadeau","createdAt":"2019-08-07T14:21:15.934Z","updatedAt":"2019-09-17T14:44:40.032Z"},{"id":83,"key":"in cart","nl":"In winkelwagen 🛒","fr":"Ajouter au panier 🛒","createdAt":"2019-08-07T15:41:17.286Z","updatedAt":"2020-01-17T12:28:58.228Z"},{"id":84,"key":"giftcards link","nl":"https://vouchers.dreamland.be/giftcards","fr":"https://vouchers.dreamland.be/fr/giftcards","createdAt":"2019-08-07T16:05:39.164Z","updatedAt":"2019-08-07T16:05:43.462Z"},{"id":85,"key":"giftcards link text","nl":"Alle geschenkkaarten","fr":"Toutes les cartes-cadeaux","createdAt":"2019-08-07T16:05:48.086Z","updatedAt":"2019-09-19T09:04:58.154Z"},{"id":86,"key":"car","nl":"","fr":"","createdAt":"2019-08-08T07:23:49.844Z","updatedAt":"2019-08-08T07:23:49.844Z"},{"id":87,"key":"carousel end title","nl":"Nóg meer keuze?","fr":"Encore plus de choix ?","createdAt":"2019-08-08T07:23:54.260Z","updatedAt":"2019-09-19T09:05:10.239Z"},{"id":88,"key":"carousel end description","nl":"Geen probleem! Er is meer:","fr":"Et voilà !","createdAt":"2019-08-08T07:24:29.997Z","updatedAt":"2019-09-19T09:05:15.551Z"},{"id":89,"key":"carousel end image","nl":"https://www.vouchers.dreamland.be","fr":"https://www.vouchers.dreamland.be","createdAt":"2019-08-08T07:24:30.043Z","updatedAt":"2019-09-19T09:05:41.220Z"},{"id":90,"key":"edit choices","nl":"Voorkeuren aanpassen","fr":"Modifier vos préférences","createdAt":"2019-08-08T15:41:32.561Z","updatedAt":"2019-09-19T09:05:47.550Z"},{"id":91,"key":"copied","nl":"Gekopieerd naar Klembord","fr":"Copié dans le Presse-papiers","createdAt":"2019-10-02T09:29:35.311Z","updatedAt":"2019-10-21T07:37:19.911Z"},{"id":92,"key":"all details","nl":"Vind alle details op ","fr":"Retrouvez tous les détails sur ","createdAt":"2019-10-02T09:30:39.737Z","updatedAt":"2019-10-21T07:37:58.782Z"},{"id":93,"key":"product page","nl":"dreamland.be","fr":"dreamland.be","createdAt":"2019-10-02T09:30:46.712Z","updatedAt":"2019-10-21T07:37:55.936Z"},{"id":94,"key":"giftcard text","nl":"Koop uw geschenkkaarten via vouchers.dreamland.be","fr":"Achetez vos cartes-cadeaux via vouchers.dreamland.be","createdAt":"2019-10-02T10:04:57.956Z","updatedAt":"2019-10-21T07:44:40.649Z"},{"id":95,"key":"giftcard","nl":"Geschenkkaart","fr":"Carte-cadeau","createdAt":"2019-10-02T10:06:03.650Z","updatedAt":"2019-10-21T07:39:27.866Z"},{"id":96,"key":"giftcard messenger text","nl":"Koop uw geschenkkaarten via onderstaande link","fr":"Achetez vos cartes-cadeaux via le lien ci-dessous","createdAt":"2019-10-03T08:43:36.320Z","updatedAt":"2019-10-21T07:40:13.870Z"},{"id":97,"key":"specifications","nl":"Specificaties","fr":"Spécifications","createdAt":"2019-10-03T10:11:12.405Z","updatedAt":"2019-10-21T07:35:18.485Z"},{"id":98,"key":"confirm engagor","nl":"Wil je dat ik er een medewerker bij haal?","fr":"Est-ce que vous voulez que j'appelle un employé?","createdAt":"2019-10-03T12:03:21.647Z","updatedAt":"2019-10-21T07:46:08.934Z"},{"id":100,"key":"yes","nl":"Ja","fr":"Oui","createdAt":"2019-10-03T12:03:54.615Z","updatedAt":"2019-10-21T07:30:47.527Z"},{"id":101,"key":"no","nl":"Nee","fr":"Non","createdAt":"2019-10-03T12:03:58.707Z","updatedAt":"2019-10-21T07:30:50.643Z"},{"id":102,"key":"copy link","nl":"Kopieer url","fr":"Copier l'URL","createdAt":"2019-10-04T08:25:58.548Z","updatedAt":"2019-10-21T07:40:32.362Z"},{"id":103,"key":"read more","nl":"Lees meer","fr":"Lire plus","createdAt":"2019-10-04T08:26:31.185Z","updatedAt":"2019-10-21T07:40:53.023Z"},{"id":104,"key":"choices text","nl":"Pas hieronder je voorkeuren aan.","fr":"Ajustez vos préférences ci-dessous.","createdAt":"2019-10-04T13:08:12.345Z","updatedAt":"2019-10-21T07:41:05.450Z"},{"id":105,"key":"sale","nl":"PROMO","fr":"PROMO","createdAt":"2019-10-04T13:08:17.195Z","updatedAt":"2019-10-21T07:35:05.622Z"},{"id":106,"key":"promo","nl":"PROMO","fr":"PROMO","createdAt":"2019-10-04T13:08:19.618Z","updatedAt":"2019-10-21T07:35:09.348Z"},{"id":108,"key":"cart error text","nl":"Dit artikel kon niet aan je winkelmandje worden toegevoegd. Probeer later opnieuw.","fr":"Cet article ne peut être ajouté à votre panier. Réessayez plus tard.","createdAt":"2019-10-17T12:15:21.287Z","updatedAt":"2019-10-21T07:41:39.387Z"},{"id":109,"key":"budget doesnt matter","nl":"Maakt niet uit","fr":"Peu importe","createdAt":"2020-10-26T10:48:50.169Z","updatedAt":"2020-10-26T10:48:55.253Z"},{"id":110,"key":"budget max","nl":"Maakt niet uit","fr":"Peu importe","createdAt":"2020-10-26T10:49:07.495Z","updatedAt":"2020-10-26T10:49:07.495Z"},{"id":111,"key":"skip","nl":"Overslaan","fr":"Passer","createdAt":"2020-10-26T10:56:48.312Z","updatedAt":"2020-10-26T10:56:48.312Z"},{"id":112,"key":"intro text","nl":"Hey!\n\nOp zoek naar een cadeau maar geen inspiratie? Geen zorgen, ik kijk snel met je mee!","fr":"Bonjour!\n\nVous cherchez un cadeau mais vous n'avez pas beaucoup d'inspiration ? Vous êtes au bon endroit, je suis là pour vous donner des idées !","createdAt":"2020-10-26T10:56:51.985Z","updatedAt":"2020-11-04T13:56:17.486Z"},{"id":113,"key":"start","nl":"Let's go!","fr":"C'est parti !","createdAt":"2020-10-26T10:57:14.003Z","updatedAt":"2020-11-04T13:56:33.907Z"},{"id":114,"key":"failed to add to cart","nl":"failed to add to cart","fr":"failed to add to cart","createdAt":"2021-11-03T15:41:28.186Z","updatedAt":"2021-11-03T15:41:35.353Z"}];

var _cms_themes = [{"id":6,"name":"","backgroundImage":"/uploads/o_CqvDyjxWEid7YwRvchVyPhyL9SH3IPU2ZCske0rRk=.svg","mobileBackground":"","background":"#005ba9","avatar":null,"style":"","startTime":"2018-11-20T00:00:00.000Z","endTime":"2018-11-20T23:59:00.000Z","createdAt":"2018-11-20T14:56:59.796Z","updatedAt":"2019-12-10T14:31:47.811Z"},{"id":7,"name":"Kerst","backgroundImage":"","mobileBackground":"","background":"#005ba9","avatar":"/uploads/zKBq3HlJeGMXYnQdCo2MVUg4jmw3EZGr6IGy95fmmI0=.svg","style":"","startTime":"2021-12-08T10:00:00.000Z","endTime":"2022-01-04T06:00:00.000Z","createdAt":"2019-12-10T14:29:12.467Z","updatedAt":"2021-09-24T08:18:51.618Z"},{"id":8,"name":"Sint","backgroundImage":"/uploads/S-HdQ5FUN12PM4QvNLgfOzJaWwuSxzWriDWlj5lR6Ho=.png","mobileBackground":"/uploads/Ha0c8yJijjITvcOObAnZPO_B3-2xN1miO2U0L06wZ9A=.png","background":"#2458a1","avatar":"/uploads/4NIjeKm9BLHJFxItAAnsvBPioYy03HTplXA1fjGCXyA=.svg","style":"","startTime":"2021-10-23T10:00:00.000Z","endTime":"2021-12-07T10:00:00.000Z","createdAt":"2020-10-26T11:03:20.738Z","updatedAt":"2021-09-24T08:18:28.292Z"}];

const history = new BotHistory({ store: 'memory' });

// Bot setup
const composed = compose([
  history.middleware(),
  translate(translations),
  koaMiddleware,
]);

const bot = function(ctx) {
  return composed(ctx)
};
bot.id = 'bot';

export { ambientLogic, bot, history, template$1 as template, _cms_themes as themes, translate, translations, translator, decisionTree as tree };
